// These utils are compatible only with Alegeus Pretax accounts.
import { lowerCase } from 'lodash';
import capitalize from 'lodash/capitalize';
import groupBy from 'lodash/groupBy';

import {
  ENROLL_PENDING_PRETAX_COMMUTER_ACCOUNT,
  FsaStorePretaxAccountType,
  PRETAX_COMMUTER_ACCOUNT_TYPES,
  PRETAX_HSA_ACCOUNT_TYPES,
  PretaxAccountName,
  PretaxAccountStatus,
  PretaxAccountType,
  PretaxAccountV2Type,
  PretaxWalletStatus,
  TWIC_PRETAX_STORES_CONSTANTS,
} from 'constants/accounts';
import { PretaxV2AccountStatus } from 'constants/status';

import dayjs from './date-helper';

export const getAccountName = (
  account_type: string,
  account_name: string,
  account_info_type?: string
) => {
  switch (account_type.toLowerCase()) {
    case PretaxAccountType.TRANSIT:
      return `${PretaxAccountName.TRANSIT}${
        account_info_type === 'TRP' ? ' Post-Tax' : ''
      }`;
    case PretaxAccountType.PARKING:
      return `${PretaxAccountName.PARKING}${
        account_info_type === 'PKP' ? ' Post-Tax' : ''
      }`;
    case PretaxAccountType.OTHER:
      return account_name;
    default:
      return account_name;
  }
};

export const getExternalStoreLink = ({
  store_name,
}: {
  store_name?: string;
}) => {
  switch (store_name) {
    case TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.AMAZON.LINK;
    case TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.CVS.LINK;
    case TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.LINK;
    case TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.LINK;
    case TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME:
      return TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.LINK;
    default:
      return TWIC_PRETAX_STORES_CONSTANTS.AMAZON.LINK;
  }
};

export const getPretaxAccountStores = ({
  pretax_account,
}: {
  pretax_account: PretaxAccountType;
}) => {
  const external_stores: { [key in PretaxAccountType]?: Array<string> } = {
    [PretaxAccountType.LPFSA]: [
      TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PretaxAccountType.FSA]: [
      TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PretaxAccountType.LPF]: [
      TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PretaxAccountType.HSA]: [
      TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PretaxAccountType.WCS]: [
      TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
    [PretaxAccountType.LPFSAHSA]: [
      TWIC_PRETAX_STORES_CONSTANTS.FSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.HSASTORE.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.AMAZON.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.WALGREENS.NAME,
      TWIC_PRETAX_STORES_CONSTANTS.CVS.NAME,
    ],
  };
  return external_stores[pretax_account];
};

export const filterActiveFormaPretaxAccounts = <
  T extends {
    wallet_type_id: string;
    status?: string;
    company_wallet_configuration: { active: boolean; start_date: string };
  }
>({
  pretax_accounts,
}: {
  pretax_accounts: Array<T>;
}) => {
  const today = dayjs();

  return pretax_accounts
    .filter(({ company_wallet_configuration, status }) =>
      status
        ? status === PretaxV2AccountStatus.ACTIVE
        : company_wallet_configuration && company_wallet_configuration.active
    )
    .filter(
      ({ wallet_type_id }) =>
        wallet_type_id === PretaxAccountV2Type.FSA ||
        wallet_type_id === PretaxAccountV2Type.LPFSA ||
        wallet_type_id === PretaxAccountV2Type.HSA
    )
    .filter(({ company_wallet_configuration: { start_date } }) =>
      today.isSameOrAfter(dayjs(start_date))
    );
};
export const filterActiveAlegeusPretaxAccountsWithFsaType = <
  T extends { account_status_code: string; account_type: string }
>({
  pretax_accounts,
}: {
  pretax_accounts: Array<T>;
}) =>
  pretax_accounts
    .filter(
      ({ account_status_code }) =>
        account_status_code === PretaxAccountStatus.ACTIVE
    )
    .filter(({ account_type }) =>
      Object.values(FsaStorePretaxAccountType).includes(
        account_type as FsaStorePretaxAccountType
      )
    );

export const excludeTerminatedFormaAccountsForEmployees = <
  T extends {
    status?: string;
    company_wallet_configuration: { active: boolean };
  }
>({
  pretax_accounts,
}: {
  pretax_accounts: Array<T>;
}) =>
  pretax_accounts.filter(({ company_wallet_configuration, status }) => {
    const is_account_terminated = status
      ? status === PretaxV2AccountStatus.TERMINATED
      : company_wallet_configuration && !company_wallet_configuration.active;
    return !is_account_terminated;
  });

export const excludeTerminatedAlegeusAccountsForTerminatedEmployees = <
  T extends { account_status_code: string }
>({
  pretax_accounts,
  is_terminated: is_employee_terminated,
}: {
  pretax_accounts: Array<T>;
  is_terminated: boolean;
}) =>
  pretax_accounts.filter(({ account_status_code }) => {
    const is_account_terminated =
      account_status_code === PretaxAccountStatus.TERMINATED;
    return !(is_account_terminated && is_employee_terminated);
  });

export const getHsaCashBalance = <
  T extends {
    wallet_type_id: string;
    status?: string;
    amount: number;
  }
>({
  employeeWallets,
}: {
  employeeWallets: Array<T>;
}) => {
  const wallet = employeeWallets.filter(
    (wallet) =>
      wallet.wallet_type_id === PretaxAccountType.HSA &&
      wallet.status === PretaxWalletStatus.ACTIVE
  )[0];
  return wallet ? wallet.amount : 0;
};

export const getPretaxAccountLabel = ({
  account_type,
}: {
  account_type: PretaxAccountType | string;
}) => {
  switch (account_type.toLowerCase()) {
    case PretaxAccountType.WCS:
      return PretaxAccountName.WCS;
    case PretaxAccountType.LPF:
      return PretaxAccountName.LPF;
    default:
      return account_type;
  }
};

export const groupPretaxAccountsByType = <T extends { account_type: string }>(
  pretaxAccounts: Array<T>
) => {
  const groupByType = groupBy(pretaxAccounts, (acc) => acc.account_type);

  return Object.keys(groupByType)
    .sort()
    .map((key) => ({
      account_type: key,
      accounts: groupByType[key].flat(),
    }));
};

export const resolveNextEnrollWindow = ({
  cutoffDayOfMonth,
  planStartDate,
}: {
  planStartDate: string;
  cutoffDayOfMonth: number;
}) => {
  const todayOfMonth = dayjs().date();

  if (
    dayjs().isBefore(
      dayjs(planStartDate).subtract(1, 'month').date(cutoffDayOfMonth)
    )
  ) {
    return {
      changeStart: dayjs(planStartDate).format('ll'),
      enrollDeadline: dayjs(planStartDate)
        .subtract(1, 'month')
        .date(cutoffDayOfMonth)
        .format('ll'),
    };
  }

  if (todayOfMonth > cutoffDayOfMonth) {
    return {
      changeStart: dayjs().add(2, 'month').startOf('month').format('ll'),
      enrollDeadline: dayjs()
        .add(1, 'month')
        .date(cutoffDayOfMonth)
        .format('ll'),
    };
  }

  return {
    changeStart: dayjs().add(1, 'month').startOf('month').format('ll'),
    enrollDeadline: dayjs().date(cutoffDayOfMonth).format('ll'),
  };
};

export const isFSATypeAccount = ({
  account_type,
  account_info_type,
}: {
  account_type: string;
  account_info_type: string;
}) => {
  if (
    Object.values([
      PretaxAccountType.FSA,
      PretaxAccountType.LPFSA,
      PretaxAccountType.LPFSAHSA,
      PretaxAccountType.LPF,
    ]).some((search) => search === account_type.toLowerCase())
  ) {
    // FSA type accounts
    return true;
  }

  if (
    account_type.toLowerCase() === PretaxAccountType.OTHER &&
    (account_info_type === 'DCA' || account_info_type === 'LFS')
  ) {
    //LFS or DCFSA
    //Cannot remove this DCA check because is it being used on AccountItem listing page
    return true;
  }

  return false;
};

export const isDCAFSATypeAccount = ({
  account_type,
  account_info_type,
}: {
  account_type: string;
  account_info_type: string;
}) =>
  account_type.toLowerCase() === PretaxAccountType.OTHER &&
  account_info_type === 'DCA';

export const isCommuterTypeAccount = (account_type: string) =>
  PRETAX_COMMUTER_ACCOUNT_TYPES.includes(account_type);

export const isHSATypeAccount = (account_type: string) =>
  PRETAX_HSA_ACCOUNT_TYPES.includes(account_type);

export const getAccountDescription = ({
  account_type,
  account_info_type,
  company_id,
}: {
  account_type: string;
  account_info_type: string;
  company_id: string;
}) => {
  if (
    isFSATypeAccount({ account_type, account_info_type }) &&
    //we need to seperate the DCFSA account from the rest
    !isDCAFSATypeAccount({ account_type, account_info_type })
  ) {
    return company_id === 'dmt' || company_id === 'shopify'
      ? 'Use pretax funds to pay for dental, vision, and post-deductible medical expenses for you and your dependents.'
      : 'Use pretax funds to pay for dental and vision expenses for you and your dependents.';
  } else if (account_type?.toLowerCase() === PretaxAccountType.TRANSIT) {
    return 'Use pretax funds to pay for work-related transit expenses.';
  } else if (account_type?.toLowerCase() === PretaxAccountType.PARKING) {
    return 'Use pretax funds to pay for work-related parking expenses.';
  } else if (isHSATypeAccount(account_type)) {
    return 'Use pretax funds to pay for medical, dental, and vision expenses for you and your dependents.';
  } else if (isDCAFSATypeAccount({ account_type, account_info_type })) {
    return 'Use pretax funds to pay for dependent care expenses.';
  }

  return '';
};

export const isClaimEligiblePretaxAccount = <
  T extends {
    account_type_class_code: string;
    flex_account_id: string;
    account_detail_info: {
      available_balance: number;
      submit_claims_last_date?: string;
    };
  }
>({
  flex_account_id,
  account_type_class_code,
  account_detail_info: { submit_claims_last_date },
}: T) => {
  if (lowerCase(account_type_class_code) === PretaxAccountType.HSA) {
    // HSA accounts are ineligible
    return false;
  }

  if (
    flex_account_id === ENROLL_PENDING_PRETAX_COMMUTER_ACCOUNT.flex_account_id
  ) {
    // Commuter Enrollment accounts are ineligible
    return false;
  }

  if (
    submit_claims_last_date &&
    dayjs(submit_claims_last_date).endOf('day').isBefore(dayjs())
  ) {
    // Accounts passed last date to submit claims
    return false;
  }

  return true;
};

export const isPretaxAccount = <
  T extends { flex_account_id: string; account_type_class_code: string }
>({
  pretax_accounts,
  account_id,
}: {
  pretax_accounts: Array<T>;
  account_id?: string;
}) =>
  account_id &&
  pretax_accounts.some(
    ({ flex_account_id }: { flex_account_id: string }) =>
      flex_account_id === account_id
  );

export const isFSAAccountByWalletId = <
  T extends { flex_account_id: string; account_type_class_code: string }
>({
  pretax_accounts,
  account_id,
}: {
  pretax_accounts: Array<T>;
  account_id?: string;
}) => {
  const found_pretax_account = pretax_accounts.find(
    ({ flex_account_id }: { flex_account_id: string }) =>
      flex_account_id === account_id
  );
  return found_pretax_account?.account_type_class_code === 'FSA';
};

export const isPretaxV2Account = ({
  employee_wallets,
  employee_wallet_id,
  wallet_type,
}: {
  employee_wallets?: Array<{
    id: string;
    wallet_type_id: string;
  }>;
  employee_wallet_id?: string;
  wallet_type?: string;
}) => {
  const pretax_accounts_set = new Set<string>(
    Object.values(PretaxAccountV2Type)
  );
  if (employee_wallet_id) {
    const selected_wallet = employee_wallets?.find(
      ({ id }: { id: string }) => id === employee_wallet_id
    );

    return selected_wallet?.wallet_type_id
      ? pretax_accounts_set.has(selected_wallet.wallet_type_id)
      : false;
  }

  return wallet_type ? pretax_accounts_set.has(wallet_type) : false;
};

//HSA pretaxV2 employee wallets
export const isHSAAccount = ({
  employee_wallets,
  employee_wallet_id,
  wallet_type,
}: {
  employee_wallets?: Array<{
    id: string;
    wallet_type_id: string;
  }>;
  employee_wallet_id?: string;
  wallet_type?: string;
}) => {
  if (employee_wallet_id) {
    const selected_wallet = employee_wallets?.find(
      ({ id }: { id: string }) => id === employee_wallet_id
    );

    return selected_wallet?.wallet_type_id === PretaxAccountV2Type.HSA;
  }

  return wallet_type === PretaxAccountV2Type.HSA;
};

//pretaxV2 FSA only employee wallets
export const isPretaxV2FSAAccount = ({
  employee_wallets,
  employee_wallet_id,
  wallet_type,
}: {
  employee_wallets?: Array<{
    id: string;
    wallet_type_id: string;
  }>;
  employee_wallet_id?: string;
  wallet_type?: string;
}) => {
  if (employee_wallet_id) {
    const selected_wallet = employee_wallets?.find(
      ({ id }: { id: string }) => id === employee_wallet_id
    );

    return (
      selected_wallet?.wallet_type_id === PretaxAccountV2Type.FSA ||
      selected_wallet?.wallet_type_id === PretaxAccountV2Type.DCFSA ||
      selected_wallet?.wallet_type_id === PretaxAccountV2Type.LPFSA
    );
  }

  return (
    wallet_type === PretaxAccountV2Type.FSA ||
    wallet_type === PretaxAccountV2Type.DCFSA ||
    wallet_type === PretaxAccountV2Type.LPFSA
  );
};

//pretaxV2 FSA only employee wallets
export const isPretaxV2FsaOrLpfsa = ({
  employeeWallets,
  employeeWalletId,
}: {
  employeeWallets?: Array<{
    id: string;
    wallet_type_id: string;
  }>;
  employeeWalletId?: string;
}) => {
  if (employeeWalletId) {
    const selected_wallet = employeeWallets?.find(
      ({ id }: { id: string }) => id === employeeWalletId
    );

    return (
      selected_wallet?.wallet_type_id === PretaxAccountV2Type.FSA ||
      selected_wallet?.wallet_type_id === PretaxAccountV2Type.LPFSA
    );
  }
  return false;
};

export const isPretaxAccountsOnly = ({
  employee_wallets,
}: {
  employee_wallets: Array<{
    funded_by?: string;
  }>;
}) =>
  employee_wallets.every(
    ({ funded_by }) => funded_by === 'Customer & member funded'
  );

// source of truth: PretaxPlanType
// https://github.com/joinforma/pretax-services/blob/608afd893a3c73fa5ab93085b3df749f3d5d7e60/libs/accounts-domain/src/constants.ts
const PRETAX_WALLET_TYPE_IDS = [
  PretaxAccountV2Type.DCFSA,
  PretaxAccountV2Type.FSA,
  PretaxAccountV2Type.HSA,
  PretaxAccountV2Type.LPFSA,
  PretaxAccountV2Type.PARKING,
  PretaxAccountV2Type.TRANSIT,
];

export const isPretaxWallet = (acct: { wallet_type_id: string }) =>
  PRETAX_WALLET_TYPE_IDS.includes(acct.wallet_type_id as PretaxAccountV2Type);

export const translateWalletTypeToDisplay = (
  wallet_type: PretaxAccountV2Type
): string => {
  switch (wallet_type) {
    case PretaxAccountV2Type.DCFSA:
    case PretaxAccountV2Type.FSA:
    case PretaxAccountV2Type.HSA:
    case PretaxAccountV2Type.LPFSA:
      return wallet_type.toUpperCase();

    default:
      return capitalize(wallet_type);
  }
};

export const hasAlegeusHsaAccount = (
  accounts?: Array<{ account_type: string }>
) =>
  !!accounts?.find(
    ({ account_type }) => account_type.toLowerCase() === PretaxAccountType.WCS
  );

export const hasFsaLpf = <
  PretaxAccount extends { account_type: string },
  PretaxWallet extends { wallet_type_id: string }
>({
  hasFormaPretax,
  pretaxAccounts,
}: {
  hasFormaPretax: boolean;
  pretaxAccounts: Array<PretaxWallet> | Array<PretaxAccount>;
}) =>
  Boolean(
    hasFormaPretax
      ? (pretaxAccounts as Array<PretaxWallet>).find(
          ({ wallet_type_id }) => wallet_type_id === PretaxAccountV2Type.LPFSA
        )
      : (pretaxAccounts as Array<PretaxAccount>).find(
          ({ account_type }) =>
            account_type.toUpperCase() === FsaStorePretaxAccountType.LPF ||
            account_type.toUpperCase() === FsaStorePretaxAccountType.LPFSA ||
            account_type.toUpperCase() === FsaStorePretaxAccountType.LFS
        )
  );

export const getFsaOrLpfsaCategoryConfig = () => {
  const fsaOrLpfsaCategoryConfig = [
    {
      label: 'Medical',
      categoryValue: 'Medical Services',
      subCategoryValue: 'Office Visit',
    },
    {
      label: 'Dental',
      categoryValue: 'Dental Services',
      subCategoryValue: 'Dental Treatment',
    },
    {
      label: 'Vision',
      categoryValue: 'Vision Services',
      subCategoryValue: 'Office Visit',
    },
    {
      label: 'Pharmacy',
      categoryValue: 'Medical Products',
      subCategoryValue: 'Medicines and Drugs OTC',
    },
    {
      label: 'Hospital',
      categoryValue: 'Medical Services',
      subCategoryValue: 'Hospital Services',
    },
    {
      label: 'Medical Travel',
      categoryValue: 'Transit',
      subCategoryValue: 'Medical Transportation',
    },
    {
      label: 'Adoption',
      categoryValue: 'Adoption',
      subCategoryValue: 'Adoption - Medical Expense',
    },
  ];
  return fsaOrLpfsaCategoryConfig;
};
